<template>
  <header id="lid-mobile-navigation" class="sticky top-0 z-50">
    <!-- Mobile Menu -->
    <nav class="md:hidden w-full bg-app-lid--yellow-casablanca bg-paw-thin">
      <div class="relative z-50 bg-app-lid--yellow-casablanca bg-paw-thin flex items-center justify-between p-4">
        <AtomLink :url="homeUrl" title="Home">
          <AtomBrandLidLogo class="h-12" />
        </AtomLink>
        <AtomButton id="lid-mobile-navigation-btn-cta-call" :url="'tel:144'" button-type="lid-primary" class="flex shrink-0">{{ t_('menu-cta-mobile-title') }}</AtomButton>
        <AtomButton
          id="lid-mobile-navigation-btn-open-menu"
          button-type="lid-secondary"
          class="group cursor-pointer rounded-xl overflow-hidden"
          @click.prevent="overlayStore.setMobileMenu()"
        >
          <AtomIcon :icon="overlayStore.mobileMenu ? 'x' : 'hamburger'" class="w-4 h-4 text-app-lid--blue-smalt group-hover:text-white group-hover:bg-app-lid--blue-smalt" />
        </AtomButton>
      </div>

      <div
        id="lid-mobile-navigation-menu-panel"
        class="submenu-margins submenu-folded absolute z-10 bottom-0 left-0 px-4 w-full bg-app-lid--blue-prussian overflow-x-hidden overflow-y-scroll"
        :class="{ 'submenu-unfolded': overlayStore.mobileMenu }"
      >
        <ul class="mt-8 flex flex-col items-center">
          <li v-for="(item, key) in main" :key="key" class="w-full sm:w-1/2 md:w-1/3 mb-4">
            <AtomLink v-if="item.title" :url="item.url" :title="item.title" class="group w-full cursor-pointer flex items-center">
              <div
                class="text-white flex leading-snug border-b-2 group-hover:border-white border-transparent transform transition ease-in-out duration-200"
                :class="{ 'font-bold': item.isActive }"
              >
                {{ item.title }}
              </div>
              <AtomIconArrowRight class="text-white flex inline-block ml-auto w-3 h-3" />
            </AtomLink>
          </li>
        </ul>
      </div>
    </nav>

    <!-- Desktop Menu -->
    <nav class="hidden md:flex bg-app-lid--yellow-casablanca bg-paw-thin menu-header relative z-10 items-center justify-between p-4 lg:px-6 2xl:gap-32 xl:px-12 w-full">
      <AtomLink :url="homeUrl" title="Home">
        <AtomBrandLidLogo class="menu-header__logo-height" />
      </AtomLink>
      <ul v-if="main.length" class="flex items-center leading-snug gap-8 lg:gap-12 xl:gap-20 2xl:gap-32 lg:text-xl xl:text-2xl">
        <li v-for="(item, key) in main" :key="key" class="relative cursor-pointer">
          <AtomLink
            :url="item.url"
            class="transform transition ease-in-out duration-200 border-b-2 hover:border-white border-transparent"
            :class="{ 'text-app-lid--blue-prussian font-bold': item.isActive }"
            >{{ item.title }}</AtomLink
          >
        </li>
      </ul>
      <AtomButton v-if="cta && cta.url" :url="cta.url" button-type="lid-primary" class="menu-header__button flex shrink-0 lg:text-xl xl:text-2xl">
        {{ cta.title }}
      </AtomButton>
    </nav>
  </header>
</template>

<script setup lang="ts">
import { useOverlayStore } from '~/store/overlay'
import { useNavigationStore } from '~/store/navigation'
import { getHomeUrl, setupSiteConfig } from '~/composables/craft'
import { setupTranslationPrefixer } from '~/composables/util'

const route = useRoute()
const overlayStore = useOverlayStore()
const navigationStore = useNavigationStore()
const { domain } = setupSiteConfig()
const { url: homeUrl } = getHomeUrl(route)

const { t_ } = setupTranslationPrefixer('LidMenu', domain)
const main = computed(() => navigationStore.main)
const cta = computed(() => navigationStore.cta)
</script>

<style lang="scss" scoped>
.menu-header {
  @screen xl {
    min-height: 128px;
  }

  @screen 2xl {
    min-height: 164px;
  }
}

.menu-header__logo-height {
  height: 56px;

  @screen md {
    min-height: 68px;
  }

  @screen lg {
    min-height: 82px;
  }

  @screen 2xl {
    min-height: 82px;
  }
}

.menu-header__button {
  @screen 2xl {
    min-height: 84px;
  }
}

.c-nav-padding {
  padding-bottom: 82px;
}

.submenu-margins {
  top: 80px;
}

.submenu-folded {
  height: 0;
  transition: 0.5s ease-in-out all;

  &::after {
    content: '';
    display: block;
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    border-radius: 12px;
    z-index: -1;
    background-image: url('/images/LID/paw.svg');
    background-size: 100vh;
    background-repeat: no-repeat;
    background-position: top 30vh right -40vh;
    opacity: 0.3;
  }
}

.submenu-unfolded {
  height: calc(100vh - 80px);
}

.slide-menu {
  top: 162px;
}

.slide-in {
  display: block;
  min-width: 100%;
  animation: slideIn 0.25s linear;
  animation-fill-mode: forwards;
}

.slide-out {
  display: block;
  min-width: 100%;
  animation: slideOut 0.25s linear;
  animation-fill-mode: forwards;
}

@keyframes slideIn {
  0% {
    left: 100%;
  }
  100% {
    left: 0;
  }
}

@keyframes slideOut {
  0% {
    left: 0;
  }
  100% {
    left: 100%;
  }
}
</style>
